/**
 * Service worker registration and the safe update flow (ADR 0019):
 *   - a new build installs in the background and WAITS (it never takes over a running page by itself);
 *   - the console shows "Yeni sürüm var, yenile"; on click the waiting worker is activated and every open
 *     tab reloads once, on the new version (BroadcastChannel);
 *   - the first install never reloads the page.
 * Only in production builds (`vite dev` has no sw.js).
 */
type Listener = () => void;

let updateReady: (() => void) | null = null;
const listeners = new Set<Listener>();
const emit = () => listeners.forEach((l) => l());

/** Subscribe to "an update is waiting". Returns an unsubscribe function. */
export function onUpdateReady(l: Listener): () => void {
  listeners.add(l);
  return () => listeners.delete(l);
}

/** The pending update's "apply" action, or null. */
export function pendingUpdate(): (() => void) | null {
  return updateReady;
}

/** Test hook: announce an update with the given apply action. */
export function announceUpdate(apply: () => void): void {
  updateReady = apply;
  emit();
}

type Channel = { postMessage(m: unknown): void; addEventListener(t: "message", fn: (e: MessageEvent) => void): void };
export const UPDATE_CHANNEL = "fts-sw-update";

/**
 * Whether this tab reloads when the new service worker takes control: once ANY tab accepted the update
 * (BroadcastChannel), every open tab reloads exactly once onto the new version; a tab that never heard of
 * an accepted update (e.g. the first install's clients.claim) does not. Exported for tests.
 */
export function updateCoordinator(channel: Channel | null) {
  let accepted = false;
  channel?.addEventListener("message", (e) => {
    if ((e.data as { type?: string } | null)?.type === "accepted") accepted = true;
  });
  return {
    accept(): void {
      accepted = true;
      channel?.postMessage({ type: "accepted" });
    },
    /** On controllerchange: true at most once per accepted update. */
    takeReload(): boolean {
      if (!accepted) return false;
      accepted = false;
      return true;
    },
  };
}

export function registerServiceWorker(): void {
  if (!import.meta.env.PROD || typeof navigator === "undefined" || !("serviceWorker" in navigator)) return;
  const sw = navigator.serviceWorker;
  let channel: Channel | null = null;
  try {
    channel = typeof BroadcastChannel === "function" ? new BroadcastChannel(UPDATE_CHANNEL) : null;
  } catch {
    channel = null;
  }
  const coordinator = updateCoordinator(channel);
  const offer = (worker: ServiceWorker) =>
    announceUpdate(() => {
      coordinator.accept();
      worker.postMessage({ type: "SKIP_WAITING" });
    });
  sw.addEventListener("controllerchange", () => {
    // Only after a tab accepted the update; the first install (clients.claim) must not reload.
    if (coordinator.takeReload()) window.location.reload();
  });
  // A notification click with an open console: go to the alarm in this tab.
  sw.addEventListener("message", (e: MessageEvent) => {
    const m = e.data as { type?: string; url?: string } | null;
    if (m?.type !== "FTS_NAVIGATE" || typeof m.url !== "string") return;
    try {
      const u = new URL(m.url, window.location.origin);
      if (u.origin === window.location.origin && u.hash && u.hash !== window.location.hash) window.location.hash = u.hash;
    } catch {
      /* ignore */
    }
  });
  const start = async () => {
    try {
      const reg = await sw.register("/sw.js", { scope: "/", updateViaCache: "none" });
      if (reg.waiting && sw.controller) offer(reg.waiting);
      reg.addEventListener("updatefound", () => {
        const w = reg.installing;
        w?.addEventListener("statechange", () => {
          if (w.state === "installed" && sw.controller) offer(w);
        });
      });
      // Long-open consoles (control rooms) check for a new version hourly and when shown again.
      setInterval(() => void reg.update().catch(() => undefined), 60 * 60 * 1000);
      document.addEventListener("visibilitychange", () => {
        if (document.visibilityState === "visible") void reg.update().catch(() => undefined);
      });
    } catch (e) {
      console.warn("service worker registration failed", e instanceof Error ? e.message : e);
    }
  };
  if (document.readyState === "complete") void start();
  else window.addEventListener("load", () => void start(), { once: true });
}
